import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { useImageEditor } from "../components/image-editor/ImageEditorProvider";
import {
  Trophy,
  Sparkles,
  Globe,
  Mic,
  Music,
  ArrowLeft,
  ArrowRight,
  Upload,
  CheckCircle2,
  Crown,
  Users,
  Star,
  Heart,
  Lock,
  Play,
  Camera,
  Briefcase,
  Award,
  Rocket,
  Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const TALENT_OPTIONS = [
  "Singer", "Rapper", "Music Producer", "Songwriter", "DJ", "Band", "Multi-Talented Artist"
];

const GENRE_OPTIONS = [
  "Pop", "K-Pop", "Hip-Hop", "EDM", "Rock", "Country", "R&B", "Classical", "Latin",
  "Afrobeats", "Reggae", "Jazz", "Indie", "Other"
];

const empty = {
  legal_name: "", stage_name: "", country: "", city: "", date_of_birth: "",
  email: "", phone: "", website: "",
  social_links: { instagram: "", tiktok: "", youtube: "", x: "" },
  primary_talent: "", genres: [], biography: "", vision: "",
  headshot_url: "", full_body_url: "", performance_video_url: "", intro_video_url: "",
  optional_songs: [], optional_music_videos: [], optional_live_performances: [], optional_press_features: [],
  owns_music: false, has_merchandise: false, performs_live: false,
  current_fan_count: 0, sells_products_or_services: false,
  income_goals_2027: "", world_impact: ""
};

const Step = ({ idx, currentStep, label }) => (
  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
      ${currentStep === idx ? "bg-[#EC008C] text-black shadow-[0_0_20px_rgba(255,51,0,0.5)]" :
        currentStep > idx ? "bg-[#EC008C]/30 text-[#EC008C]" : "bg-[#1A2030] text-[#8A92A0]"}`}>
      {currentStep > idx ? <CheckCircle2 className="w-4 h-4" /> : idx}
    </div>
    <span className={`text-xs sm:text-sm font-medium hidden md:inline ${currentStep === idx ? "text-white" : "text-[#8A92A0]"}`}>
      {label}
    </span>
  </div>
);

const Top100Idols = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/idol-2027/applications/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          if (r.data) {
            setExisting(r.data);
            setForm({ ...empty, ...r.data, social_links: r.data.social_links || empty.social_links });
          } else if (user?.email) {
            setForm(f => ({ ...f, email: user.email }));
          }
        }).catch(() => {});
    }
  }, [isAuthenticated, token, user]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateSocial = (k, v) => setForm(f => ({ ...f, social_links: { ...f.social_links, [k]: v } }));
  const toggleGenre = (g) => setForm(f => ({
    ...f, genres: f.genres.includes(g) ? f.genres.filter(x => x !== g) : [...f.genres, g]
  }));

  const editImage = useImageEditor();
  const handleUpload = async (e, field) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (!isAuthenticated) { toast.error("Please sign in to upload media"); return; }
    file = await editImage(file, { maxBytes: 50 * 1024 * 1024 });
    if (!file) { e.target.value = ""; return; }
    setUploading(u => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/community/upload-media`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      update(field, res.data.url || res.data.media_url || "");
      toast.success("Uploaded \u2713");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(u => ({ ...u, [field]: false }));
    }
  };

  const submit = async () => {
    if (!isAuthenticated) {
      toast.error("Sign in first to submit your application");
      navigate("/auth?redirect=/top-100-global-idols-2027");
      return;
    }
    if (!form.legal_name || !form.stage_name || !form.email || !form.primary_talent || !form.biography) {
      toast.error("Please fill in the required fields marked with *");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, current_fan_count: parseInt(form.current_fan_count) || 0 };
      const res = await axios.post(`${API}/idol-2027/apply`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.updated ? "Application updated \ud83c\udf89" : "Application sent to StardomFans admin \ud83c\udf89");
      setExisting({ ...form, application_id: res.data.application_id });
      setStep(6);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // LANDING
  if (step === 0) {
    return (
      <div className="min-h-screen bg-[#050810] text-white" data-testid="top100-landing">
        <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            </Link>
            <Link to="/home"><Button variant="ghost" className="text-[#8A92A0] hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Button></Link>
          </div>
        </header>

        <main className="relative pt-28 pb-20 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#EC008C]/20 rounded-full blur-[120px]" />
            <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-[#EC008C]/10 rounded-full blur-[150px]" />
          </div>

          <div className="relative max-w-5xl mx-auto px-6">
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#EC008C]/20 to-[#FF3399]/20 border border-[#EC008C]/40 backdrop-blur-md">
                <Crown className="w-4 h-4 text-[#EC008C]" />
                <span className="text-sm text-[#EC008C] font-semibold tracking-widest uppercase">Presented by StardomFans.com</span>
              </div>
            </div>

            <h1 className="font-['Unbounded'] text-5xl sm:text-7xl lg:text-8xl font-black text-center mb-6 leading-[1.05]" data-testid="hero-title">
              Top <span className="text-[#EC008C] italic">100</span><br />
              Global Music Idols<br />
              of <span className="bg-gradient-to-r from-[#EC008C] via-[#FF3399] to-[#EC008C] bg-clip-text text-transparent">2027</span>
            </h1>

            <p className="text-center font-['Unbounded'] text-xl sm:text-2xl italic text-[#EC008C] mb-10" data-testid="hero-tagline">
              &ldquo;One Passion. Infinite Stardom.&rdquo;
            </p>

            <div className="card p-8 sm:p-10 mb-12 bg-gradient-to-br from-[#EC008C]/10 via-transparent to-[#EC008C]/5 border-[#EC008C]/40 text-center" data-testid="free-application-banner">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8DB87A]/15 text-[#8DB87A] text-xs font-bold tracking-widest mb-5">
                <Sparkles className="w-3.5 h-3.5" /> 100% FREE TO APPLY
              </div>
              <h2 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold mb-4 text-white">
                100% Free Global Application
              </h2>
              <p className="text-[#8A92A0] text-lg leading-relaxed max-w-2xl mx-auto mb-6">
                Open to <span className="text-white font-semibold">singers, rappers, producers, songwriters, bands, DJs, and music creators</span> from every country.
                <br /><br />
                Apply now to become one of the <span className="text-[#EC008C] font-bold">Top 100 Global Music Idols of 2027</span>.
              </p>
              <Button
                onClick={() => setStep(1)}
                className="bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:scale-105 font-bold rounded-full px-8 sm:px-10 h-14 text-base sm:text-lg shadow-[0_0_30px_rgba(255,51,0,0.4)] transition-all"
                data-testid="apply-now-btn"
              >
                <Rocket className="w-5 h-5 mr-2" />
                Apply Now &mdash; Free
              </Button>
            </div>

            <section className="mb-16">
              <h3 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold mb-6 text-white text-center">Not Just Another Talent Show</h3>
              <p className="text-[#8A92A0] text-lg leading-relaxed text-center max-w-3xl mx-auto">
                We&apos;re not looking for who sings best. We&apos;re looking for the <span className="text-white font-semibold">100 artists who can build the biggest empires</span> and the greatest global impact on fans, culture, and the future of music.
              </p>
            </section>

            <section className="mb-16">
              <h3 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold mb-8 text-white text-center">The Road to the Top 100</h3>
              <div className="space-y-3 max-w-2xl mx-auto">
                {[
                  ["Round 1", "10,000+ Applications", "Open Global Submissions"],
                  ["Round 2", "Top 1,000", "Video & Bio Review"],
                  ["Round 3", "Top 500", "Empire Builder Assessment"],
                  ["Round 4", "Top 250", "Public Showcase & Fan Engagement"],
                  ["Round 5", "Top 100", "Final Selection \u2014 Global Music Idols of 2027"],
                ].map(([round, count, desc], i) => (
                  <div key={round} className="card p-4 sm:p-5 flex items-center gap-4 hover:border-[#EC008C]/40 transition-colors" data-testid={`round-${i+1}`}>
                    <div className="w-12 h-12 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
                      <span className="font-['Unbounded'] text-lg font-bold text-[#EC008C]">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Unbounded'] font-bold text-white text-sm sm:text-base">{round} &middot; {count}</p>
                      <p className="text-xs sm:text-sm text-[#8A92A0]">{desc}</p>
                    </div>
                    {i === 4 && <Crown className="w-5 h-5 text-[#EC008C] flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h3 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold mb-8 text-white text-center">What the Top 100 Receive</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  [Star, "Verified StardomFans Profile"],
                  [Briefcase, "Built-in Artist Store"],
                  [Heart, "Dedicated Fan Club"],
                  [Mic, "Public Booking Page"],
                  [Music, "Global Music Distribution"],
                  [Award, "Sponsorship Opportunities"],
                  [Zap, "Homepage Featured Placement"],
                  [Trophy, "Official Digital Certification"],
                ].map(([Icon, label], i) => (
                  <div key={i} className="card p-4 flex items-center gap-3" data-testid={`benefit-${i}`}>
                    <Icon className="w-5 h-5 text-[#EC008C] flex-shrink-0" />
                    <span className="text-white text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h3 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold mb-8 text-white text-center">How We Score</h3>
              <div className="card p-6 sm:p-8 max-w-xl mx-auto">
                {[
                  ["Talent", 25], ["Originality", 20], ["Stage Presence", 15],
                  ["Fan Engagement", 15], ["Entrepreneurial Ability", 15], ["Global Potential", 10],
                ].map(([k, w]) => (
                  <div key={k} className="flex items-center gap-4 mb-3 last:mb-0" data-testid={`scoring-${k.toLowerCase().replace(/\s/g,'-')}`}>
                    <span className="w-44 sm:w-52 text-[#8A92A0] text-sm">{k}</span>
                    <div className="flex-1 h-2 bg-[#1A2030] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#EC008C] to-[#FF3399]" style={{ width: `${w * 4}%` }} />
                    </div>
                    <span className="font-['Unbounded'] font-bold text-[#EC008C] text-sm w-10 text-right">{w}%</span>
                  </div>
                ))}
              </div>
            </section>

            <div className="text-center">
              <Button
                onClick={() => setStep(1)}
                className="bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:scale-105 font-bold rounded-full px-12 h-14 text-lg shadow-[0_0_40px_rgba(255,51,0,0.5)] transition-all"
                data-testid="apply-cta-bottom"
              >
                Start My Application
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-xs text-[#6A7280] mt-4">100% free &middot; One-page application &middot; Editable until results are announced</p>
            </div>
          </div>
        </main>

        <footer className="py-12 px-6 border-t border-[#1A2030]">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <div className="flex gap-6 flex-wrap justify-center">
              <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
              <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
              <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
              <Link to="/status" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Status</Link>
            </div>
            <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
          </div>
        </footer>
      </div>
    );
  }

  // SUCCESS
  if (step === 6) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6" data-testid="application-success">
        <div className="max-w-xl w-full text-center card p-10">
          <div className="w-20 h-20 rounded-full bg-[#EC008C]/15 border-2 border-[#EC008C] flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-[#EC008C]" />
          </div>
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-3">Application Locked In</h1>
          <p className="text-[#8A92A0] mb-6">
            Your application is in. We&apos;ll announce Round 2 candidates by email. You can return to <Link to="/dashboard" className="text-[#EC008C] hover:underline">your dashboard</Link> at any time to edit your application until results are posted.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/dashboard"><Button className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full px-6" data-testid="back-dashboard-btn">Go to Dashboard</Button></Link>
            <Button variant="outline" onClick={() => setStep(0)} className="rounded-full px-6 border-[#1A2030] text-white hover:bg-[#1A2030]" data-testid="back-landing-btn">View Landing</Button>
          </div>
        </div>
      </div>
    );
  }

  // SINGLE-PAGE FORM (all sections collapsed into one scrollable page)
  return (
    <div className="min-h-screen bg-[#050810] text-white pb-16" data-testid="top100-form">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2"><img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} /></Link>
          <Button variant="ghost" onClick={() => setStep(0)} className="text-[#8A92A0] hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        </div>
      </header>

      <main className="pt-28 max-w-3xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 mb-4">
            <Crown className="w-4 h-4 text-[#EC008C]" />
            <span className="text-xs text-[#EC008C] font-bold tracking-widest uppercase">Top 100 Global Music Idols 2027</span>
          </div>
          <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-white">Your Application</h1>
          <p className="text-[#8A92A0] text-sm mt-2">One page. One submission. Sent directly to the StardomFans admin inbox for review.</p>
        </div>

        {!isAuthenticated && (
          <div className="card p-4 mb-6 border-[#EC008C]/40 bg-[#EC008C]/5 flex items-start gap-3" data-testid="signin-notice">
            <Lock className="w-5 h-5 text-[#EC008C] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-white font-semibold mb-1 text-sm">Sign in to save and submit</p>
              <p className="text-[#8A92A0] text-xs">You can fill the form now, but you&apos;ll need a free StardomFans account to submit and edit. <Link to="/auth?redirect=/top-100-global-idols-2027" className="text-[#EC008C] hover:underline font-semibold">Sign in / Create account</Link></p>
            </div>
          </div>
        )}

        {/* SECTION 1 — Basic Info */}
        <section className="card p-6 sm:p-8 space-y-5 mb-6" data-testid="section-basic">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center text-[#EC008C] font-bold text-sm">1</div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Tell us about you</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Legal Name *</Label><Input data-testid="input-legal-name" value={form.legal_name} onChange={e => update("legal_name", e.target.value)} /></div>
            <div><Label>Stage Name *</Label><Input data-testid="input-stage-name" value={form.stage_name} onChange={e => update("stage_name", e.target.value)} /></div>
            <div><Label>Country *</Label><Input data-testid="input-country" value={form.country} onChange={e => update("country", e.target.value)} /></div>
            <div><Label>City *</Label><Input data-testid="input-city" value={form.city} onChange={e => update("city", e.target.value)} /></div>
            <div><Label>Date of Birth *</Label><Input data-testid="input-dob" type="date" value={form.date_of_birth} onChange={e => update("date_of_birth", e.target.value)} /></div>
            <div><Label>Email *</Label><Input data-testid="input-email" type="email" value={form.email} onChange={e => update("email", e.target.value)} /></div>
            <div><Label>Phone *</Label><Input data-testid="input-phone" value={form.phone} onChange={e => update("phone", e.target.value)} /></div>
            <div><Label>Website (optional)</Label><Input data-testid="input-website" placeholder="https://" value={form.website} onChange={e => update("website", e.target.value)} /></div>
          </div>
          <div>
            <Label className="block mb-2">Social Media Links</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="input-instagram" placeholder="Instagram URL" value={form.social_links.instagram} onChange={e => updateSocial("instagram", e.target.value)} />
              <Input data-testid="input-tiktok" placeholder="TikTok URL" value={form.social_links.tiktok} onChange={e => updateSocial("tiktok", e.target.value)} />
              <Input data-testid="input-youtube" placeholder="YouTube URL" value={form.social_links.youtube} onChange={e => updateSocial("youtube", e.target.value)} />
              <Input data-testid="input-x" placeholder="X / Twitter URL" value={form.social_links.x} onChange={e => updateSocial("x", e.target.value)} />
            </div>
          </div>
        </section>

        {/* SECTION 2 — Artist Identity */}
        <section className="card p-6 sm:p-8 space-y-5 mb-6" data-testid="section-profile">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center text-[#EC008C] font-bold text-sm">2</div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Your artist identity</h2>
          </div>
          <div>
            <Label className="block mb-2">Primary Talent *</Label>
            <div className="flex flex-wrap gap-2">
              {TALENT_OPTIONS.map(t => (
                <button key={t} type="button" onClick={() => update("primary_talent", t)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${form.primary_talent === t ? "bg-[#EC008C] text-black border-[#EC008C]" : "bg-transparent text-[#8A92A0] border-[#1A2030] hover:border-[#EC008C]"}`}
                  data-testid={`talent-${t.toLowerCase().replace(/\s/g,'-')}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="block mb-2">Music Genres (pick all that apply)</Label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map(g => (
                <button key={g} type="button" onClick={() => toggleGenre(g)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${form.genres.includes(g) ? "bg-[#EC008C] text-black border-[#EC008C]" : "bg-transparent text-[#8A92A0] border-[#1A2030] hover:border-[#EC008C]"}`}
                  data-testid={`genre-${g.toLowerCase().replace(/[\s/]+/g,'-')}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Biography (300 words max) *</Label>
            <Textarea data-testid="input-biography" rows={5} maxLength={2500} placeholder="Tell us your story in 300 words or less." value={form.biography} onChange={e => update("biography", e.target.value)} />
            <p className="text-xs text-[#8A92A0] mt-1">{form.biography.length} / 2500 chars</p>
          </div>
          <div>
            <Label>Your Vision *</Label>
            <Textarea data-testid="input-vision" rows={3} maxLength={1000} placeholder="Why do you deserve to become one of the Top 100 Global Music Idols of 2027?" value={form.vision} onChange={e => update("vision", e.target.value)} />
          </div>
        </section>

        {/* SECTION 3 — Media */}
        <section className="card p-6 sm:p-8 space-y-4 mb-6" data-testid="section-media">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center text-[#EC008C] font-bold text-sm">3</div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Show us who you are</h2>
          </div>
          <p className="text-sm text-[#8A92A0]">Upload high-quality media. Required: headshot, full body photo, performance video (3 min max), intro video (60s).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["headshot_url", "Headshot *", Camera, "image/*"],
              ["full_body_url", "Full Body Photo *", Camera, "image/*"],
              ["performance_video_url", "Performance Video (3 min max) *", Play, "video/*"],
              ["intro_video_url", "Introduction Video (60s) *", Play, "video/*"],
            ].map(([field, label, Icon, accept]) => (
              <div key={field} className="card p-4 border-dashed" data-testid={`upload-${field}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-[#EC008C]" />
                  <Label className="text-sm">{label}</Label>
                </div>
                {form[field] ? (
                  <div className="text-xs text-[#8DB87A] mb-2 break-all"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Uploaded</div>
                ) : null}
                <label className="cursor-pointer">
                  <input data-testid={`idol-file-${field}`} type="file" accept={accept} className="hidden" onChange={e => handleUpload(e, field)} disabled={uploading[field]} />
                  <div className={`px-3 py-2 rounded-lg border border-[#1A2030] hover:border-[#EC008C] text-xs text-white text-center transition-colors ${uploading[field] ? "opacity-50" : ""}`}>
                    <Upload className="w-3.5 h-3.5 inline mr-1.5" />
                    {uploading[field] ? "Uploading..." : (form[field] ? "Replace" : "Choose file")}
                  </div>
                </label>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 4 — Empire */}
        <section className="card p-6 sm:p-8 space-y-5 mb-6" data-testid="section-empire">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center text-[#EC008C] font-bold text-sm">4</div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Empire Builder Assessment</h2>
          </div>
          <p className="text-sm text-[#8A92A0]">We&apos;re looking for artists who can build sustainable empires, not just one-hit wonders.</p>
          <div className="space-y-3">
            {[
              ["owns_music", "Do you own your music?"],
              ["has_merchandise", "Do you have merchandise?"],
              ["performs_live", "Do you perform live?"],
              ["sells_products_or_services", "Do you sell products or services?"],
            ].map(([field, q]) => (
              <div key={field} className="flex items-center justify-between gap-3 card p-4">
                <span className="text-white text-sm">{q}</span>
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={() => update(field, true)} className={`rounded-full ${form[field] ? "bg-[#8DB87A] text-black" : "bg-[#1A2030] text-[#8A92A0]"}`} data-testid={`${field}-yes`}>Yes</Button>
                  <Button type="button" size="sm" onClick={() => update(field, false)} className={`rounded-full ${!form[field] ? "bg-[#EC008C] text-black" : "bg-[#1A2030] text-[#8A92A0]"}`} data-testid={`${field}-no`}>No</Button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label>Current Fan Count (approximate, across all platforms)</Label>
            <Input data-testid="input-fan-count" type="number" min="0" value={form.current_fan_count} onChange={e => update("current_fan_count", e.target.value)} />
          </div>
          <div>
            <Label>Income Goals for 2027</Label>
            <Textarea data-testid="input-income-goals" rows={3} placeholder="What revenue, fan reach, or career milestones do you aim to hit in 2027?" value={form.income_goals_2027} onChange={e => update("income_goals_2027", e.target.value)} />
          </div>
        </section>

        {/* SECTION 5 — Impact */}
        <section className="card p-6 sm:p-8 space-y-5 mb-6" data-testid="section-impact">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center text-[#EC008C] font-bold text-sm">5</div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Your impact on the world</h2>
          </div>
          <div className="card p-5 bg-[#EC008C]/5 border-[#EC008C]/30">
            <p className="text-white font-semibold mb-2">If you become one of the Top 100 Global Music Idols of 2027, what impact will you make on the world?</p>
            <p className="text-xs text-[#8A92A0]">Your answer becomes part of your candidate story &mdash; used for marketing, documentaries, and final selection.</p>
          </div>
          <Textarea data-testid="input-world-impact" rows={8} maxLength={3000} placeholder="Write from the heart. What changes? What gets lifted up? Whose lives are different because of your music and platform?" value={form.world_impact} onChange={e => update("world_impact", e.target.value)} />
        </section>

        {/* SUBMIT */}
        <div className="sticky bottom-4 z-10 flex items-center justify-center pt-2">
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-gradient-to-r from-[#EC008C] to-[#FF3399] text-black hover:scale-105 font-bold rounded-full px-10 h-14 text-base shadow-[0_0_30px_rgba(255,51,0,0.5)] transition-all"
            data-testid="submit-application-btn"
          >
            {submitting ? "Sending to admin inbox..." : (existing ? "Update My Application" : "Submit to StardomFans Admin")}
          </Button>
        </div>
        <p className="text-center text-xs text-[#6A7280] mt-3">Submissions land in the admin inbox. You can edit anytime until results are announced.</p>
      </main>
    </div>
  );
};

export default Top100Idols;
